import type { KeyboardEvent } from 'react';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { LuChevronRight, LuTrash2 } from 'react-icons/lu';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { IconButton } from '@/components/ui/icon-button';
import { Tooltip } from '@/components/ui/tooltip';
import PersonAvatar from '@/components/common/PersonAvatar';
import type { ArchiveEntry } from '@/components/services/model/contactArchive';

interface ArchiveEntryRowProps {
	entry: ArchiveEntry;
	houseId: number;
	propertyPublicId: string;
	/**
	 * Puts the entry back, where the row sits on the Archive page and that is the one thing to do
	 * with it. Absent on the Historical tab: there the row opens the drawer, and the drawer has the
	 * way back among its actions, so a second one on the row was a second target on one line.
	 */
	onRestore?: () => void;
	isRestoring?: boolean;
	/**
	 * Opens the entry's drawer, where the row sits on the contacts page and the entry is a contact.
	 * With it the whole row is the button, as a current contact's row is; without it, on the
	 * Archive page, the row is a record with a Restore on it.
	 */
	onOpen?: () => void;
	/** The row whose drawer is open, so the list says which one. */
	isSelected?: boolean;
	/**
	 * Offers to delete the entry for good, where the row sits on the Archive rather than on the
	 * contacts page. Disabled with a reason while the API has no permanent delete to send.
	 */
	deleteForever?: { onDelete?: () => void; unavailable?: string };
}

/**
 * One entry in the archive, whichever list it came from.
 *
 * The Archive's selected view already names deleted contacts and discarded candidates, so each
 * row does not repeat that state in a badge. Historical rows open the contact drawer instead.
 *
 * A Historical row rests on the tint and lifts to white under the pointer, the reverse of a current
 * contact's row: the same inversion the schedule uses to make History read as a different kind of
 * list before a word of it is read. The open one is white too, and outlined in brand, so hovering
 * and being open never look the same.
 *
 * Restore is a button rather than a hover action. The whole page is a restore list, and the only
 * thing there is to do should not have to be found by pointing at it.
 */
const ArchiveEntryRow = (props: ArchiveEntryRowProps) => {
	const { entry, onRestore, isRestoring = false, onOpen, isSelected = false, deleteForever } = props;
	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (!onOpen || (event.key !== 'Enter' && event.key !== ' ')) return;
		event.preventDefault();
		onOpen();
	};

	return (
		<Flex
			align="center"
			gap={3}
			py={3}
			px={{ base: 3, md: 4 }}
			borderTopWidth="1px"
			borderColor="border.default"
			// The card draws the top edge; a first row with its own doubled it. The end rows take the
			// card's corners (`layerStyle="card"` is 2xl), so a fill or an outline on them follows the
			// curve instead of being clipped square by it.
			_first={{ borderTopWidth: 0, borderTopRadius: '2xl' }}
			_last={{ borderBottomRadius: '2xl' }}
			{...(onOpen
				? {
						role: 'button',
						tabIndex: 0,
						'aria-label': `Open ${entry.name}`,
						'aria-current': isSelected ? ('true' as const) : undefined,
						cursor: 'pointer',
						bg: isSelected ? 'surface.default' : 'surface.subtle',
						// The open row is outlined in brand, as the schedule outlines its open occurrence;
						// inset, because the rows are flush and an outer border would push its neighbours.
						boxShadow: isSelected ? 'inset 0 0 0 1px var(--chakra-colors-brand-solid)' : undefined,
						onClick: onOpen,
						onKeyDown: handleKeyDown,
						_hover: { bg: 'surface.default' },
						_focusVisible: { outline: '2px solid', outlineColor: 'focus.ring', outlineOffset: '-2px' },
						transition: 'background-color 0.15s ease',
					}
				: {})}
		>
			<PersonAvatar name={entry.name} email={entry.email ?? undefined} size="40px" fontSize="sm" />
			<Box flex="1" minW={0}>
				<Flex align="center" gap={2} minW={0}>
					<Text textStyle="body.sm" fontWeight="medium" truncate>
						{entry.name}
					</Text>
				</Flex>
				{entry.detail ? (
					<Text textStyle="body.sm" color="fg.muted" truncate>
						{entry.detail}
					</Text>
				) : null}
				{/* Where a contact was found is left out for now: surfacing it gets messy quickly. */}
			</Box>
			{/* Named, because a column of identical "Restore" buttons tells a screen reader nothing
			    about which person each one puts back. */}
			{onRestore ? (
				<OutlinedButton
					size="sm"
					w="auto"
					minW="auto"
					px={4}
					flexShrink={0}
					aria-label={`Restore ${entry.name}`}
					onClick={onRestore}
					loading={isRestoring}
				>
					Restore
				</OutlinedButton>
			) : null}
			{deleteForever ? (
				<Tooltip content={deleteForever.unavailable ?? `Delete ${entry.name} permanently`}>
					<IconButton
						size="sm"
						variant="ghost"
						color="fg.error"
						flexShrink={0}
						aria-label={`Delete ${entry.name} permanently`}
						disabled={!deleteForever.onDelete}
						onClick={deleteForever.onDelete}
					>
						<LuTrash2 />
					</IconButton>
				</Tooltip>
			) : null}
			{/* The same affordance a current contact's row carries: a row that opens has to look like one. */}
			{onOpen ? (
				<Flex w="40px" h="40px" align="center" justify="center" flexShrink={0}>
					<Icon as={LuChevronRight} boxSize={4} color="brand.fg" />
				</Flex>
			) : null}
		</Flex>
	);
};

export default ArchiveEntryRow;
